import { createDemoState, createEmptyState } from '../domain/demo';
import { DomainError, reduceCommand, type CommandActor } from '../domain/reducer';
import type { CareCommand, CareState, CommandResult, Role } from '../domain/types';
import { ApiError } from './api';
import { CacheConflict, clearCareCache, readCareCache, stripDogs, writeCareCache, type OutboxEntry } from './careCache';

export interface CareUser { id: string; email?: string; name?: string }
export interface CareStoreSnapshot { state: CareState; ready: boolean; isSampleData: boolean; pendingCount: number; syncError: string | null; syncNotice: string | null; lastSyncedAt: string | null; conflicts: { command: CareCommand; message: string }[] }
export interface CareRemote { fetchState(): Promise<CareState>; sendCommand(command: CareCommand): Promise<CommandResult> }
export function getDogRole(state: CareState, dogId: string, user: CareUser | null, mode: 'demo' | 'connected'): Role {
  if (mode === 'demo') return 'owner';
  if (user && !dogId && !state.dogs.length) return 'owner';
  const email = user?.email?.toLowerCase();
  return state.grants.find(grant => grant.dogId === dogId && grant.status === 'active' && grant.email.toLowerCase() === email)?.role || 'vet';
}

/** Serializes device writes and HTTP acknowledgements so rapid taps cannot erase each other. */
export class CareStoreEngine {
  private queue: OutboxEntry[] = [];
  private listeners = new Set<() => void>();
  private serial: Promise<unknown> = Promise.resolve();
  private flushPromise: Promise<void> | null = null;
  private disposed = false;
  private online = true;
  private sequence = 0;
  private revokedDogs = new Set<string>();
  private cacheRevision = 0;
  private broadcast: BroadcastChannel | null = null;
  private authoritative: CareState;
  private serverGeneration = 0;
  private refreshRequest = 0;
  private value: CareStoreSnapshot;
  constructor(readonly scope: string, readonly mode: 'demo' | 'connected', readonly user: CareUser | null, private remote?: CareRemote) {
    this.value = { state: mode === 'demo' ? createDemoState() : createEmptyState(), ready: false, isSampleData: mode === 'demo', pendingCount: 0, syncError: null, syncNotice: null, lastSyncedAt: null, conflicts: [] };
    this.authoritative = this.value.state;
    this.startBroadcast();
  }
  private startBroadcast() {
    if (this.broadcast) return;
    if (typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined') {
      this.broadcast = new BroadcastChannel(`pawlog:${this.scope}`);
      this.broadcast.onmessage = () => { if (!this.disposed) void this.transaction(async () => undefined); };
    }
  }
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  getSnapshot = () => this.value;
  private emit(updates: Partial<CareStoreSnapshot>) {
    if (this.disposed) return;
    const conflicts = this.queue.filter(entry => entry.status === 'conflict').map(entry => ({ command: entry.command, message: entry.error || 'Review this change before retrying.' }));
    const syncError = updates.syncError === null && conflicts.length ? 'Saved changes need review before they can sync.' : updates.syncError === undefined ? this.value.syncError : updates.syncError;
    this.value = { ...this.value, ...updates, syncError, pendingCount: this.queue.filter(entry => entry.status === 'pending').length, conflicts };
    for (const listener of this.listeners) listener();
  }
  private transaction<T>(work: () => Promise<T>): Promise<T> {
    const operation = this.serial.then(async () => {
      for (let attempt = 0; attempt < 4; attempt++) {
        const cached = await readCareCache(this.scope);
        if (cached.snapshot && (cached.snapshot.storageRevision ?? 0) > this.cacheRevision) {
          this.cacheRevision = cached.snapshot.storageRevision ?? 0;
          this.authoritative = cached.snapshot.authoritative || cached.snapshot.state;
          this.queue = cached.outbox.filter(entry => !entry.command.dogId || !this.revokedDogs.has(entry.command.dogId));
          this.sequence = Math.max(this.sequence, ...this.queue.map(entry => entry.sequence ?? 0));
          // Preserve a vet-only memory view when another tab saves user preferences.
          const vetDogs = this.value.state.dogs.filter(dog => getDogRole(this.value.state, dog.id, this.user, this.mode) === 'vet');
          const cachedState = stripDogs(cached.snapshot.state, this.revokedDogs);
          const retainedIds = new Set(vetDogs.map(dog => dog.id));
          const retained = this.value.state;
          const state = { ...cachedState, dogs: [...cachedState.dogs, ...vetDogs], medications: [...cachedState.medications, ...retained.medications.filter(item => retainedIds.has(item.dogId))], administrations: [...cachedState.administrations, ...retained.administrations.filter(item => retainedIds.has(item.dogId))], episodes: [...cachedState.episodes, ...retained.episodes.filter(item => retainedIds.has(item.dogId))], grants: [...cachedState.grants, ...retained.grants.filter(item => retainedIds.has(item.dogId))], selectedDogId: retainedIds.has(retained.selectedDogId) ? retained.selectedDogId : cachedState.selectedDogId };
          this.emit({ state, lastSyncedAt: cached.snapshot.lastSyncedAt || null, isSampleData: cached.snapshot.isSampleData ?? this.value.isSampleData });
        }
        try { return await work(); } catch (error) { if (!(error instanceof CacheConflict) || attempt === 3) throw error; }
      }
      throw new CacheConflict();
    });
    this.serial = operation.catch(() => undefined);
    return operation;
  }
  private actor(state: CareState, command: CareCommand): CommandActor {
    const role = command.type === 'dog.save' && !state.dogs.some(dog => dog.id === command.entityId) ? 'owner' : getDogRole(state, command.dogId || state.selectedDogId, this.user, this.mode);
    return { role, name: this.user?.name || (this.mode === 'demo' ? 'Jamie' : undefined), email: this.user?.email };
  }
  private persistentState(state: CareState): CareState {
    if (this.mode === 'demo') return state;
    return stripDogs(state, [...this.revokedDogs, ...state.dogs.filter(dog => getDogRole(state, dog.id, this.user, this.mode) === 'vet').map(dog => dog.id)]);
  }
  private async persist(state: CareState, queue = this.queue, lastSyncedAt = this.value.lastSyncedAt, isSampleData = this.value.isSampleData): Promise<void> {
    this.cacheRevision = await writeCareCache(this.scope, { state: this.persistentState(state), authoritative: this.persistentState(this.authoritative), lastSyncedAt: lastSyncedAt || undefined, isSampleData }, queue, this.cacheRevision);
    this.broadcast?.postMessage({ revision: this.cacheRevision });
  }
  private rebuild(remoteState: CareState): CareState {
    remoteState = stripDogs(remoteState, this.revokedDogs);
    let state = { ...remoteState, selectedDogId: remoteState.dogs.some(dog => dog.id === this.value.state.selectedDogId) ? this.value.state.selectedDogId : remoteState.selectedDogId || remoteState.dogs[0]?.id || '' };
    for (const entry of this.queue) {
      if (entry.status === 'conflict') continue;
      // The server may have accepted a command before the response was interrupted.
      if (state.audit.some(audit => audit.id === entry.command.id)) continue;
      try { state = reduceCommand(state, entry.command, this.actor(state, entry.command)); }
      catch (error) {
        // A missing/truncated audit entry cannot distinguish an accepted command
        // from a stale edit. Replay its unchanged UUID so the server receipt decides.
        if (error instanceof DomainError && error.conflict) continue;
        entry.status = 'conflict'; entry.error = error instanceof Error ? error.message : 'This record changed.';
      }
    }
    return state;
  }
  async initialize(): Promise<void> {
    this.disposed = false;
    this.startBroadcast();
    try { await this.transaction(async () => {
      try {
        const cached = await readCareCache(this.scope);
        this.queue = cached.outbox;
        this.sequence = Math.max(0, ...this.queue.map(entry => entry.sequence ?? 0));
        if (cached.snapshot) this.emit({ state: cached.snapshot.state, lastSyncedAt: cached.snapshot.lastSyncedAt || null, isSampleData: cached.snapshot.isSampleData ?? this.mode === 'demo' });
        else await this.persist(this.value.state);
      } catch (error) { if (error instanceof CacheConflict) throw error; this.emit({ syncError: 'Device storage is unavailable. Changes cannot be saved until browser storage is enabled.' }); }
      this.emit({ ready: true });
    }); } catch { this.emit({ ready: true, syncError: 'Device storage is unavailable. Changes cannot be saved until browser storage is enabled.' }); }
    if (this.mode === 'connected' && this.online) await this.refresh();
  }
  setOnline(online: boolean) { this.online = online; if (online && this.mode === 'connected') void this.flush(); }
  async dispatch(command: CareCommand): Promise<CommandResult> {
    const result: CommandResult = await this.transaction(async () => {
      if (this.disposed) return { commandId: command.id, error: 'This care session has ended.' };
      if (command.dogId && this.revokedDogs.has(command.dogId)) return { commandId: command.id, error: 'Access to this dog was removed.' };
      if (this.queue.some(entry => entry.command.id === command.id) || this.value.state.audit.some(entry => entry.id === command.id)) return { commandId: command.id, state: this.value.state };
      try {
        let state = reduceCommand(this.value.state, command, this.actor(this.value.state, command));
        const entry: OutboxEntry = { command, status: 'pending', sequence: ++this.sequence };
        const queue = this.mode === 'connected' ? [...this.queue, entry] : this.queue;
        if (this.mode === 'demo') state = { ...state, episodes: state.episodes.map(item => ({ ...item, syncStatus: 'local' })), administrations: state.administrations.map(item => ({ ...item, syncStatus: 'local' })) };
        await this.persist(state, queue);
        this.queue = queue;
        this.emit({ state, syncError: null });
        return { commandId: command.id, state };
      } catch (error) {
        if (error instanceof CacheConflict) throw error;
        const message = error instanceof Error ? error.message : 'This change could not be saved.';
        this.emit({ syncError: message });
        return { commandId: command.id, error: message, conflict: error instanceof DomainError && error.conflict };
      }
    }).catch(error => { const message = error instanceof Error ? error.message : 'Device storage is unavailable. This change has not been saved.'; this.emit({ syncError: message }); return { commandId: command.id, error: message }; });
    if (!result.error && this.mode === 'connected' && this.online) void this.flush();
    return result;
  }
  async refresh(): Promise<void> {
    if (!this.remote || !this.online || this.disposed) return;
    const generation = this.serverGeneration;
    const request = ++this.refreshRequest;
    try {
      const remote = await this.remote.fetchState();
      await this.transaction(async () => {
        if (this.disposed || request !== this.refreshRequest || generation !== this.serverGeneration) return;
        // A read refresh that no longer includes a dog also removes cached commands for that dog.
        const visible = new Set(remote.dogs.map(dog => dog.id));
        for (const entry of this.queue) if (entry.command.type === 'dog.save' && entry.status === 'pending') visible.add(entry.command.entityId);
        this.queue = this.queue.filter(entry => !entry.command.dogId || visible.has(entry.command.dogId) || entry.command.type === 'dog.save');
        this.authoritative = stripDogs(remote, this.revokedDogs);
        const state = this.rebuild(remote);
        const at = new Date().toISOString();
        await this.persist(state, this.queue, at, false);
        this.serverGeneration++;
        this.emit({ state, syncError: null, lastSyncedAt: at, isSampleData: false });
      });
    } catch (error) { this.emit({ syncError: error instanceof Error ? error.message : 'Could not refresh the care record.' }); }
    if (this.queue.some(entry => entry.status === 'pending')) void this.flush();
  }
  async flush(): Promise<void> {
    if (this.flushPromise) return this.flushPromise;
    if (!this.remote || !this.online || this.disposed) return;
    this.flushPromise = (async () => {
      while (this.online && !this.disposed) {
        const entry = await this.transaction(async () => this.queue.find(item => item.status === 'pending'));
        if (!entry) break;
        try {
          const result = await this.remote!.sendCommand(entry.command) as CommandResult & { warning?: string; invitationEmailStatus?: string };
          if (result.error || result.conflict) throw new ApiError(result.error || 'Review this changed record before retrying.', result.conflict ? 409 : 400, result);
          const authoritative = result.state || await this.remote!.fetchState();
          await this.transaction(async () => {
            if (this.disposed) return;
            const previousQueue = this.queue;
            this.queue = this.queue.filter(item => item.command.id !== entry.command.id && (!item.command.dogId || !this.revokedDogs.has(item.command.dogId)));
            this.authoritative = stripDogs(authoritative, this.revokedDogs);
            const state = this.rebuild(authoritative);
            const at = new Date().toISOString();
            try { await this.persist(state, this.queue, at); } catch (error) { this.queue = previousQueue; throw error; }
            this.emit({ state, lastSyncedAt: at, syncError: null, syncNotice: result.warning || (entry.command.type === 'grant.invite' ? result.invitationEmailStatus === 'accepted' ? 'Invitation email submitted.' : null : this.value.syncNotice) });
            this.serverGeneration++;
          });
        } catch (error) {
          if (error instanceof ApiError && [400, 409, 422].includes(error.status)) {
            const authoritative = await this.remote!.fetchState().catch(() => undefined);
            await this.transaction(async () => {
              if (this.disposed) return;
              const live = this.queue.find(item => item.command.id === entry.command.id);
              if (live) { live.status = 'conflict'; live.error = error.message; }
              if (authoritative) this.authoritative = stripDogs(authoritative, this.revokedDogs);
              const state = authoritative ? this.rebuild(authoritative) : this.value.state;
              await this.persist(state);
              this.serverGeneration++;
              this.emit({ state, syncError: error.message });
            });
            continue;
          }
          if (error instanceof ApiError && error.status === 403 && entry.command.dogId) {
            await this.revokeDog(entry.command.dogId);
            continue;
          }
          this.emit({ syncError: error instanceof Error ? error.message : 'Sync interrupted. Your changes remain on this device.' });
          break;
        }
      }
    })().catch(error => { this.emit({ syncError: error instanceof Error ? error.message : 'Sync interrupted. Your changes remain on this device.' }); }).finally(() => { this.flushPromise = null; });
    return this.flushPromise;
  }
  async revokeDog(dogId: string, deleted = false): Promise<void> {
    // Update visible memory immediately; persistence is serialized with in-flight commands.
    this.revokedDogs.add(dogId);
    this.serverGeneration++;
    this.emit({ state: stripDogs(this.value.state, [dogId]), syncError: deleted ? null : 'Access to this dog’s care record was removed. Unsent changes were not transmitted.' });
    await this.transaction(async () => { this.queue = this.queue.filter(entry => entry.command.dogId !== dogId); this.authoritative = stripDogs(this.authoritative, [dogId]); const state = stripDogs(this.value.state, [dogId]); await this.persist(state); this.emit({ state }); });
  }
  async selectDog(dogId: string): Promise<void> {
    await this.transaction(async () => { if (!this.value.state.dogs.some(dog => dog.id === dogId)) return; const state = { ...this.value.state, selectedDogId: dogId }; await this.persist(state); this.emit({ state }); });
  }
  async resetDemo(empty = false): Promise<void> {
    if (this.mode !== 'demo') return;
    await this.transaction(async () => { const state = empty ? createEmptyState() : createDemoState(); await this.persist(state, [], null, !empty); this.queue = []; this.emit({ state, isSampleData: !empty, syncError: null, lastSyncedAt: null }); });
  }
  async resolveConflict(commandId: string, action: 'discard' | 'retry'): Promise<CommandResult | void> {
    const authoritative = this.remote && this.online ? await this.remote.fetchState() : this.authoritative;
    let retried: CareCommand | undefined;
    await this.transaction(async () => {
      const entry = this.queue.find(item => item.command.id === commandId && item.status === 'conflict');
      if (!entry) return;
      const previousQueue = this.queue;
      this.queue = this.queue.filter(item => item.command.id !== commandId);
      try {
        let state = this.rebuild(authoritative);
        if (action === 'retry') {
          const command = entry.command;
          const id = command.type === 'medication.save' ? String(command.payload.previousVersionId || command.entityId) : command.entityId;
          const entity = [...state.dogs, ...state.medications, ...state.administrations, ...state.episodes].find(item => item.id === id);
          retried = { ...command, id: crypto.randomUUID(), expectedRevision: entity?.revision ?? 0, happenedAt: new Date().toISOString() };
          state = reduceCommand(state, retried, this.actor(state, retried));
          this.queue.push({ command: retried, status: 'pending', sequence: ++this.sequence });
        }
        this.authoritative = stripDogs(authoritative, this.revokedDogs);
        await this.persist(state);
        this.emit({ state, syncError: null });
      } catch (error) { this.queue = previousQueue; throw error; }
    });
    if (retried) { if (this.online) void this.flush(); return { commandId: retried.id, state: this.value.state }; }
  }
  async clear(): Promise<void> {
    await this.transaction(async () => { await clearCareCache(this.scope, true); this.disposed = true; this.broadcast?.close(); this.broadcast = null; });
  }
  dispose() { this.disposed = true; this.listeners.clear(); this.broadcast?.close(); this.broadcast = null; }
}
